/** Continut in romana. Numele modurilor urmeaza catalogul `ro.json`. */
import type { LanguageContent } from '../registry.js';

export const RO: LanguageContent = {
  home: {
    intro: [
      'Retusch este un joc de desen online pentru 2 până la 10 jucători, se joacă în browser, fără nimic de instalat și fără cont. În fiecare rundă primești un tablou din care a fost ștearsă o zonă: repictează-o astfel încât să dispară în operă. Apoi rolurile se inversează și trebuie să scoți la iveală retușurile celorlalți.',
      'Aproape toate jocurile de desen răsplătesc pe cel recunoscut cel mai repede. Retusch cere exact contrariul: câștigi dacă nu ești văzut.',
    ],
    steps: [
      {
        title: 'Un tablou cu o gaură',
        text: 'Primești o ilustrație din care a fost ștearsă o zonă.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'O repictezi',
        text: 'Redesenezi zona ca să se contopească cu opera.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'Vânătoarea',
        text: 'Tabloul revine cu retușurile tuturor: găsește-le înaintea celorlalți.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'Dezvăluirea',
        text: 'Fiecare ascunzătoare iese la iveală, iar punctele cad.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Vânătoare comună',
        text: 'Toți pictează pe același tablou, fiecare în zona lui. Apoi vânătoarea începe pentru toți deodată: un retuș se găsește o singură dată, de cel care îl vede primul. Este modul implicit, cel în care se întâmplă mereu ceva.',
      },
      {
        name: 'Stream',
        text: 'Gazda nu desenează: caută singură, în timp ce toți ceilalți se ascund în tabloul ei. Gândit pentru direct, unde vânătoarea devine spectacolul. Gratuit pentru totdeauna.',
      },
      {
        name: 'Fiecare cu tabloul lui',
        text: 'O operă de jucător. Cauți retușul unei singure persoane, dintr-un singur clic. Cea mai simplă regulă a jocului, și cea mai tensionată.',
      },
      {
        name: 'Desenează unul singur',
        text: 'O rundă, un desenator, toți ceilalți pe urmele lui. Rolul se rotește, iar desenatorul își privește ascunzătoarea cum cade sau cum rezistă.',
      },
      {
        name: 'Battle royale',
        text: 'Aceeași vânătoare, dar primul retuș descoperit își elimină autorul. Se joacă până la ultimul rămas, fără să se numere un singur punct.',
      },
      {
        name: 'Concurs',
        text: 'Nimeni nu caută. Toți repictează aceeași zonă a aceluiași tablou, apoi fiecare votează retușul care îi place cel mai mult. Un vot primit valorează un punct.',
      },
    ],
    faq: [
      {
        question: 'De câți jucători e nevoie?',
        answer:
          'De la 2 la 10. Dacă ești singur, un adversar automat ocupă locul liber ca partida să înceapă imediat. De la patru în sus, vânătoarea comună dă tot ce are mai bun.',
      },
      {
        question: 'Este gratuit?',
        answer:
          'Da. Partida rapidă, vânătoarea comună și modul stream sunt gratuite, fără limită de timp și fără limită de partide. Și celelalte moduri sunt gratuite, deocamdată.',
      },
      {
        question: 'Am nevoie de cont?',
        answer:
          'Nu. Scrii un nume, dai clic, joci. Contul păstrează doar numele și avatarul de la o partidă la alta: rămâne opțional.',
      },
      {
        question: 'Merge pe telefon?',
        answer:
          'Da, interfața se adaptează la atingere: bară de unelte pliată, lupă sub deget, dublu tap pentru mărire. Cu toate acestea, desenezi mai precis cu mouse-ul și pe un ecran mare.',
      },
      {
        question: 'Cum îmi invit prietenii?',
        answer:
          'Creezi un salon, copiezi linkul de invitație și îl lipești unde vrei. Prietenii tăi ajung direct la masă, fără cont și fără nimic de instalat.',
      },
      {
        question: 'Ce este un „retuș”?',
        answer:
          'Este ceea ce pictezi în zona ștearsă. Reușit, trece drept o bucată din tabloul original și nimeni nu îl găsește. Ratat, sare în ochi.',
      },
    ],
    headings: {
      intro: 'Un joc de desen în care trebuie să dispari',
      steps: 'Cum se joacă',
      modes: 'Șase moduri de a juca',
      faq: 'Întrebări frecvente',
      more: 'Mai departe',
    },
    links: {
      rules: 'Citește regulile complete și punctajul',
      modes: 'Compară modurile în detaliu',
    },
  },

  howToPlay: {
    title: 'Cum se joacă Retusch',
    summary:
      'Regulile complete ale Retusch: desfășurarea unei runde, punctajul de ambele părți, uneltele de desen și sfaturi ca să te ascunzi și ca să cauți.',
    sections: [
      {
        heading: 'Ideea în trei fraze',
        blocks: [
          'Primești un tablou din care a fost ștearsă o zonă și o repictezi ca să treacă neobservată.',
          'Apoi toți privesc tablourile retușate și caută zonele repictate.',
          'Faci puncte găsind repede, și faci puncte și atâta timp cât ascunzătoarea ta rezistă.',
        ],
      },
      {
        heading: 'Desfășurarea unei runde',
        blocks: [
          'O partidă înlănțuie runde identice, cinci în mod implicit. Fiecare trece prin trei momente.',
          {
            list: [
              'Desenul. Primești tabloul tău și zona ta, și ai implicit 2 minute ca să o repictezi. Butonul Gata înseamnă predare: de îndată ce toți au predat, runda merge mai departe fără să aștepte cronometrul.',
              'Vânătoarea. Tabloul reapare cu retușurile, și ai implicit 30 de secunde ca să le găsești. Un clic pe o zonă repictată o dezvăluie întregii mese.',
              'Dezvăluirea. Toate ascunzătorile sunt încadrate și numite, inclusiv cele pe care nu le-a văzut nimeni. Un cursor permite compararea tabloului retușat cu opera originală.',
            ],
          },
        ],
      },
      {
        heading: 'Punctajul',
        blocks: [
          'Două contoare merg în paralel și se opun: cel care caută vrea să fie rapid, cel care a desenat vrea ca zona lui să reziste.',
          {
            list: [
              'Pentru cel care caută: 1000 de puncte dacă găsești în primele 3 secunde, apoi o scădere constantă până la 100 de puncte la finalul fazei. Ratarea sau nedescoperirea nimic nu aduc nimic.',
              'Pentru cel care desenează: doar 100 de puncte dacă zona ta cade imediat, apoi o creștere constantă până la 800 de puncte dacă rezistă până la capăt. Un retuș pe care nu îl găsește nimeni valorează maximul, 800 de puncte.',
            ],
          },
          'Desenatorul este plătit o singură dată, la dezvăluire, și după cel mai rapid căutător. Nu contează așadar dacă te caută doi sau opt oameni: contează doar cine te găsește primul.',
          'Punctajul este același indiferent de durata pe care o stabilești pentru fază. Prelungirea vânătorii nu mută cele două capete, doar panta dintre ele.',
          'O singură greșeală pe rundă. Primul clic care nu atinge niciun retuș îți încheie vânătoarea: fără această regulă s-ar răscoli imaginea la întâmplare până s-ar nimeri unul. Două gesturi nu costă nimic: clicul pe propria zonă și clicul pe un retuș deja găsit.',
        ],
      },
      {
        heading: 'Uneltele de desen',
        blocks: [
          {
            list: [
              'Pensula (B): trei mărimi rapide, plus un cursor pentru mărimea exactă. Scurtăturile 1, 2 și 3 aleg cele trei mărimi.',
              'Găleata (P): umple dintr-o dată o suprafață plată, fără să iasă vreodată din zona ta.',
              'Pipeta (I): ia o culoare de oriunde din tablou. Ținerea tastei Alt trece pe ea pentru un clic și redă apoi unealta anterioară.',
              'Mâna (H): deplasează vederea. În afara zonei tale, pensula și găleata fac același lucru, așa că aproape nu ai nevoie să schimbi unealta.',
              'Anulare (Ctrl+Z sau Cmd+Z): dai înapoi câte o tușă, de până la treizeci de ori. Nu există refacere.',
              'Culoarea de fundal, la capătul paletei: este culoarea de start a zonei tale. A picta cu ea înseamnă a șterge, cu diferența că este o tușă ca oricare alta, care se anulează și se acoperă. De aceea nu există gumă.',
            ],
          },
        ],
      },
      {
        heading: 'Cinci sfaturi ca să te ascunzi',
        blocks: [
          {
            list: [
              'Ia culorile cu pipeta, nu le ghici. Pipeta dă culoarea exactă a ceea ce se află în jur: o suprafață cât de cât alături se vede de departe, în timp ce una exactă devine invizibilă.',
              'Prelungește ce traversează zona ta. Un zid, un orizont, o creangă care intră pe o parte trebuie să iasă pe cealaltă exact la locul potrivit. O linie întreruptă este primul indiciu pe care îl caută un jucător experimentat.',
              'Pictează întâi colțurile. Este cea mai frecventă greșeală: centrul e îngrijit, cele patru colțuri rămân pe fundalul de start, iar pătratul se vede cu ochiul liber fără să te uiți măcar la desen.',
              'Mergi de la general la detaliu. Pune întâi masele mari de culoare cu o pensulă groasă, apoi rafinează. Invers, îți mănânci tot timpul pe un colț în timp ce restul rămâne gol.',
              'Copiază nivelul de detaliu al vecinătății. O zonă prea curată în mijlocul unei zone încărcate se vede la fel de bine ca una făcută prost în mijlocul unei suprafețe plate.',
            ],
          },
        ],
      },
      {
        heading: 'Patru sfaturi ca să cauți',
        blocks: [
          {
            list: [
              'Caută liniile care se întrerup. O margine, un cablu, colțul unei mese care se mută cu un fir de păr: aproape întotdeauna acolo este.',
              'Întâi parcurge, apoi mărește. La vedere de ansamblu, o zonă prost contopită apare ca o pată. Zoomul servește la confirmare, nu la căutare.',
              'Fii atent la ce e prea neted. O suprafață perfect uniformă în mijlocul unei texturi este la fel de suspectă ca o urmă neîngrijită.',
              'Nu da clic la întâmplare. Ai dreptul la o singură greșeală: mai bine pierzi trei secunde verificând decât runda întreagă.',
            ],
          },
        ],
      },
      {
        heading: 'Și în funcție de mod?',
        blocks: [
          'Tot ce s-a spus mai sus descrie vânătoarea comună, modul implicit. Celelalte moduri schimbă cine desenează, cine caută și cum se câștigă: detaliile fiecăruia sunt pe pagina modurilor de joc.',
        ],
      },
    ],
  },

  modes: {
    title: 'Modurile de joc din Retusch',
    summary:
      'Cele șase moduri de a juca Retusch: vânătoare comună, stream, fiecare cu tabloul lui, desenează unul singur, battle royale și concurs. Desfășurare, număr de jucători recomandat și durată.',
    sections: [
      {
        heading: 'Vânătoare comună',
        blocks: [
          'Modul implicit, și cel cu care merită să începi.',
          'Toți desenează pe același tablou, fiecare în zona lui. Când retușurile sunt predate, opera revine întreagă și vânătoarea începe pentru toți deodată. Un retuș se găsește o singură dată, de cel care îl vede primul: apare atunci pentru toată masa și iese din joc. Pe al tău nu îl găsești niciodată, iar o ascunzătoare pe care nu o vede nimeni dă maximul autorului ei.',
          'Ideal pentru un grup mare: nu există timpi morți, toți caută în același timp și fiecare descoperire apare pe ecranul tuturor.',
          'Recomandat: de la 3 la 10 jucători.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'Imaginea în oglindă a modului „desenează unul singur”, gândită pentru direct.',
          'Toți jucătorii desenează pe același tablou, în afară de gazdă. Apoi gazda este singura care caută, în timp ce publicul ei o privește cum se chinuie. Nu face puncte și nu apare în clasament: partida se joacă între cei care se ascund, ea conduce vânătoarea. Poate greși de câte ori vrea, regula greșelii unice nu se aplică în cazul ei.',
          'Ideal pentru o seară în direct, și dintr-un motiv precis: fiindcă gazda nu desenează, spectatorii nu văd niciodată cum se construiește ascunzătoarea ei. În celelalte moduri o privesc cum își pictează zona, iar vânătoarea nu mai are niciun secret pentru ei.',
          'Recomandat: de la 4 la 10 jucători. Gratuit, și va rămâne așa.',
        ],
      },
      {
        heading: 'Fiecare cu tabloul lui',
        blocks: [
          'Fiecare jucător primește opera lui și își ascunde retușul în ea. La vânătoare ți se dă tabloul unei singure alte persoane și ai un singur clic ca să arăți zona repictată. Nicio cursă, nicio concurență: doar tu, un tablou și o decizie.',
          'Ideal în doi, sau când vrei o partidă liniștită în care fiecare își ia timpul lui.',
          'Recomandat: de la 2 la 10 jucători.',
        ],
      },
      {
        heading: 'Desenează unul singur',
        blocks: [
          'O rundă, un desenator, toți ceilalți pe urmele lui.',
          'În fiecare rundă, o singură persoană primește un tablou. Ceilalți așteaptă, apoi caută retușul ei, fiecare fiind notat după timpul lui. Desenatorul este notat după cel mai rapid căutător și își revede ascunzătoarea, înghețată, în timp ce este căutată. Rolul se rotește: aici reglajul rundelor numără ture complete, fiindcă o partidă este echitabilă doar dacă fiecare a desenat de tot atâtea ori.',
          'Ideal pentru un grup mic care vrea să se măsoare în condiții egale, desen după desen.',
          'Recomandat: de la 3 la 6 jucători, peste această cifră așteptarea devine lungă.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'Vânătoarea comună, cu o eliminare pe rundă.',
          'Același tablou comun, aceeași vânătoare simultană. Dar aici nu se numără niciun punct: singura întrebare este cine rămâne. Jucătorul al cărui retuș este descoperit primul părăsește partida. Ceilalți continuă să caute până la finalul rundei, ceea ce nu mai elimină pe nimeni, dar dă fiecăruia locul lui, afișat pe zona lui. Dacă nu este găsit nimeni, nu iese nimeni.',
          'Ideal ca să închei o seară: tensiunea crește pe măsură ce masa se golește, iar ultima rundă se joacă în doi.',
          'Recomandat: de la 4 la 10 jucători.',
        ],
      },
      {
        heading: 'Concurs',
        blocks: [
          'Singurul mod în care nimeni nu caută.',
          'Toți repictează aceeași zonă a aceluiași tablou. Apoi fiecare parcurge retușurile celorlalți, în ritmul lui, și votează pentru cel care i se pare cel mai reușit. Un vot primit valorează un punct pentru autorul lui. În timpul votului autorii sunt anonimi și nu se poate vota pentru sine: numele și voturile ies abia la dezvăluire.',
          'Ideal când grupul este eterogen, sau când cineva preferă să deseneze fără presiunea cronometrului de vânătoare.',
          'Recomandat: de la 3 la 10 jucători.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Vânătoare comună', players: 'de la 3 la 10', duration: '15-20 min' },
    { name: 'Stream', players: 'de la 4 la 10', duration: '15-20 min' },
    { name: 'Fiecare cu tabloul lui', players: 'de la 2 la 10', duration: '15-20 min' },
    { name: 'Desenează unul singur', players: 'de la 3 la 6', duration: '20-35 min' },
    { name: 'Battle royale', players: 'de la 4 la 10', duration: '20-30 min' },
    { name: 'Concurs', players: 'de la 3 la 10', duration: '10-15 min' },
  ],
  modeTableHeadings: ['Mod', 'Jucători', 'Durată'],
  premium: {
    heading: 'Moduri gratuite și moduri cu plată',
    blocks: [
      'Vânătoarea comună și modul stream sunt gratuite, definitiv.',
      'Celelalte patru vor deveni cu plată la CREAREA unei partide, și doar atunci. Alăturarea va rămâne mereu gratuită: este de ajuns ca o singură persoană de la masă să fi deblocat jocul pentru ca toți să poată juca toate modurile, fără cont și fără să plătească.',
      'Deblocarea va fi o achiziție unică, fără abonament, și va scoate și reclamele pentru contul care a făcut-o. Până atunci, toate modurile sunt deschise tuturor.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'Ce este Retusch?',
      date: '2026-08-21',
      summary:
        'Un joc de desen online pentru mai multe persoane, apropiat de Gartic Phone ca formă, dar a cărui mecanică amintește de Meccha Chameleon: în loc să te faci recunoscut, încerci să dispari.',
      sections: [
        {
          heading: 'Un joc de desen în care a fi recunoscut înseamnă a pierde',
          blocks: [
            'Retusch este un joc de desen online pentru 2 până la 10 jucători. În fiecare rundă primești o ilustrație din care a fost ștearsă o zonă și trebuie să o repictezi ca să se contopească cu opera. Apoi toți privesc tablourile retușate și caută zonele repictate.',
            'Ce îl deosebește încape într-o frază: câștigi dacă nu ești văzut.',
          ],
        },
        {
          heading: 'Ce împarte cu Gartic Phone',
          blocks: [
            'Forma este aceeași: deschizi un link, scrii un nume, joci. Fără cont, fără instalare, fără client de descărcat. Jocul rulează în browser și o partidă pornește în câteva secunde. Îți inviți prietenii lipind un link într-o conversație, iar masa se umple.',
            'Și ritmul este familiar: runde scurte, un cronometru, o fază de desen și apoi o fază în care descoperi ce au produs ceilalți. Și mai ales acea dezvăluire colectivă de la finalul rundei, în care toți comentează ce văd. Este aceeași plăcere de grup.',
            'Ca și în Gartic Phone, nu e nevoie să știi să desenezi. O zonă de cer bine pusă bate o capodoperă care iese din cadru.',
          ],
        },
        {
          heading: 'Ce ia din Meccha Chameleon',
          blocks: [
            'În adâncime însă, mecanica este în altă parte. În Meccha Chameleon, joc tridimensional pe Steam, îți pictezi personajul și apoi îl plasezi într-un decor, sperând ca nimeni să nu îl deosebească de ce îl înconjoară. Victoria vine din asemănare și din plasarea bună, nu din acțiune.',
            'Retusch reia această idee și o aduce înapoi la imagine. Nu alegi unde te ascunzi, zona ți-o dă jocul; în schimb fabrici în întregime ce va intra în ea. Ascunzătoarea reușește dacă privirea alunecă peste ea fără să se oprească.',
            'Și ca în orice joc de mimetism, tensiunea vine din rolul dublu. Ești în același timp ascuns și vânător: în timp ce scrutezi tabloul căutând o suprafață suspectă, cineva îl scrutează pe al tău.',
          ],
        },
        {
          heading: 'Ce nu există nicăieri altundeva',
          blocks: [
            'Punctajul răsplătește cele două tabere în sens opus. Cel care caută face cu atât mai multe puncte cu cât găsește mai repede; cel care a desenat face cu atât mai multe cu cât zona lui rezistă mai mult. Un retuș pe care nu îl vede nimeni dă maximul autorului său.',
            'De aici se naște un mod de a juca foarte aparte: nu cauți să faci frumos, cauți să faci exact. Să iei culoarea potrivită, să prelungești o linie exact unde trebuie, să nu lași niciun colț gol. Sunt gesturi de observație înainte de a fi gesturi de desen.',
            'Ca să înțelegi în practică, cel mai simplu este să citești regulile complete sau să lansezi o partidă rapidă: pornește fără reglaje și fără așteptare.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'Cum să devii de neînvins la Retusch',
      date: '2026-08-21',
      summary:
        'Scurtăturile de tastatură, metoda de la general la detaliu și greșeala colțurilor goale: trei lucruri care despart un începător de un jucător pe care nu îl mai găsește nimeni.',
      sections: [
        {
          heading: 'Învață scurtăturile și îți dublezi timpul util',
          blocks: [
            'Două minute de desen trec foarte repede când iei fiecare unealtă cu mouse-ul. Scurtăturile recuperează ușor douăzeci sau treizeci de secunde pe rundă, și exact acelea sunt secundele pe care le vei da detaliilor.',
            {
              list: [
                'B pentru pensulă, P pentru găleată, I pentru pipetă, H pentru mână.',
                '1, 2 și 3 pentru cele trei mărimi de pensulă, fără să treci prin bara de unelte.',
                'Ctrl+Z, sau Cmd+Z pe Mac, ca să anulezi câte o tușă.',
                'Ținerea tastei Alt trece pe pipetă și redă unealta anterioară de îndată ce o eliberezi. Este cea mai utilă dintre toate: iei o culoare în plină acțiune fără să schimbi vreodată unealta.',
              ],
            },
            'O precizare pentru tastaturile AZERTY: tastele 1, 2 și 3 funcționează fără Shift, chiar dacă tastatura ta scrie acolo &, é și ". Jocul citește poziția tastei, nu caracterul pe care îl produce.',
          ],
        },
        {
          heading: 'Pictează de la general la detaliu',
          blocks: [
            'Greșeala clasică de începător este să pornești dintr-un colț și să îl îngrijești. Cronometrul se termină, trei sferturi din zonă sunt încă fundal, iar retușul se vede de la zece metri.',
            'Metoda bună este exact pe dos. Începe cu o pensulă groasă, sau cu găleata, și pune masele mari de culoare: cerul, zidul, umbra de pe jos. În douăzeci de secunde zona ta are deja aspectul general potrivit și nu se mai citește ca un bloc străin.',
            'Abia atunci micșorezi mărimea și tratezi trecerile: linia orizontului care traversează, marginea unui obiect, degradeul care se schimbă la jumătatea înălțimii. Dacă timpul lipsește, vei fi pierdut detaliu, nu runda.',
            'Ia întotdeauna culorile cu pipeta în loc să le alegi din paletă. O nuanță aproximativă este de ajuns ca să trădeze o zonă întreagă, în timp ce nuanța exactă o face invizibilă.',
          ],
        },
        {
          heading: 'Nu lăsa niciodată colțurile goale',
          blocks: [
            'Este indiciul numărul unu, și primul pe care îl caută jucătorii experimentați. O zonă cu centrul bine tratat, dar cu cele patru colțuri rămase pe culoarea de fundal desenează un dreptunghi perfect pe ecran. Nici măcar nu trebuie să te uiți la desen: forma geometrică sare în ochi.',
            'Ia-ți obiceiul de a trata întâi conturul și abia apoi centrul. O ramă bine contopită iartă un interior aproximativ; invers nu este niciodată adevărat.',
            'Ieși dinadins peste margini. Jocul îți taie tușa la limita zonei, deci nu riști nimic mergând mult mai departe, și este singurul mod de a fi sigur că niciun pixel de fundal nu supraviețuiește într-un colț.',
          ],
        },
        {
          heading: 'Și când e rândul tău să cauți',
          blocks: [
            'Parcurge întâi tabloul întreg fără să mărești: o zonă prost contopită arată ca o pată, iar zoomul nu servește decât la confirmare. Apoi urmărește liniile lungi, cele care traversează imaginea: o margine mutată cu un fir de păr este aproape întotdeauna un retuș.',
            'Și ține minte că ai dreptul la o singură greșeală pe rundă. Trei secunde de verificare costă mai puțin decât runda întreagă.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'Un joc de desen de jucat între prieteni',
      date: '2026-08-21',
      summary:
        'De ce Retusch merge bine într-un grup mic, cum inviți lipind un link și de ce este de ajuns un singur jucător ca să deblocheze modurile cu plată pentru toată masa.',
      sections: [
        {
          heading: 'Un joc care nu cere nimic de la nimeni',
          blocks: [
            'Cel mai mare obstacol al unei seri de jocuri la distanță este instalarea. Un cont de creat, un client de descărcat, o versiune care nu se potrivește: jumătate din grup renunță înainte de a juca.',
            'Retusch rulează în browser. Prietenii tăi dau clic pe un link, scriu un nume și se trezesc la masă. Nu e nimic de instalat, nimic de creat, și merge bine atât pe calculator, cât și pe telefon.',
            'Încă ceva care contează într-un grup: nimeni nu rămâne pe dinafară pentru că nu știe să deseneze. Jocul nu cere să reprezinți ceva, doar să potrivești o zonă cu ce o înconjoară. Cine desenează prost, dar observă bine, câștigă deseori.',
          ],
        },
        {
          heading: 'Invitația, în practică',
          blocks: [
            'Creează un salon din ecranul de pornire. Debifează „salon public” dacă vreți să rămâneți între voi: invitația prin link devine atunci singura cale de intrare.',
            'Copiază linkul cu butonul dedicat, apoi lipește-l în conversația voastră de grup, în canalul vostru vocal sau în mesageria voastră. Conține codul salonului: prietenii tăi aterizează direct la masa ta.',
            'Poți regla partida în timp ce lumea sosește: modul, numărul de runde, timpul de desen, mărimea zonei și chiar categoriile de imagini. Nimic nu este fixat până nu lansezi.',
          ],
        },
        {
          heading: 'Unul plătește, toți joacă',
          blocks: [
            'Două moduri sunt gratuite pentru totdeauna: vânătoarea comună, care este cel implicit, și modul stream.',
            'Celelalte vor deveni cu plată, dar doar la CREAREA unei partide. Alăturarea va rămâne gratuită, fără condiții. În practică este de ajuns ca o singură persoană din grup să fi deblocat jocul pentru ca toată masa să se bucure de toate modurile, fără cont și fără să plătească nimic.',
            'Deblocarea este o achiziție unică, fără abonament.',
          ],
        },
        {
          heading: 'Ce mod pentru ce grup',
          blocks: [
            'În trei sau patru, vânătoarea comună este de ajuns și se înțelege dintr-o rundă. De la șase în sus devine cu adevărat bună: descoperirile se succed și ecranul nu se mai oprește din mișcare.',
            'Ca să încheiați o seară, încercați battle royale: masa se golește rundă după rundă, iar ultima se joacă în doi. Pentru un grup eterogen, în care cineva preferă să deseneze pe îndelete, concursul scoate toată presiunea cronometrului de vânătoare.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'Un joc de desen de jucat în direct',
      date: '2026-08-21',
      summary:
        'Retusch are un mod gândit pentru direct, în care streamerul caută singur în timp ce publicul lui se ascunde. Cum se folosește și ce alte moduri merg în fața unui public.',
      sections: [
        {
          heading: 'Un mod făcut anume',
          blocks: [
            'Modul stream inversează rolurile. Toți jucătorii desenează pe același tablou în afară de gazdă, care nu desenează deloc. Apoi gazda este singura care caută, în timp ce publicul ei privește.',
            'Este exact situația pe care o cere un direct, și dintr-un motiv precis: fiindcă gazda nu desenează, ascunzătoarea ei nu există, iar chatul nu a văzut nimic din ce nu trebuia să vadă. Spectatorii cunosc în schimb ascunzătorile celorlalți jucători și îl pot privi cum trece pe lângă ele de trei ori la rând.',
            'Pentru acest caz au fost scrise două reguli. Streamerul nu face puncte și nu apare în clasament: partida se joacă între cei care se ascund, el conduce spectacolul. Și poate greși de câte ori vrea, în timp ce toate celelalte moduri se opresc la prima greșeală. Un direct nu se poate încheia pe un clic nefericit.',
            'Modul stream este gratuit și va rămâne așa.',
          ],
        },
        {
          heading: 'Concursul, a doua idee bună',
          blocks: [
            'În modul concurs, toți repictează aceeași zonă a aceluiași tablou, apoi fiecare votează retușul care îi place cel mai mult. Nu mai este nimic de găsit, doar de judecat.',
            'În fața unui public merge foarte bine, și din același motiv ca modul stream: nu este nimic de găsit, deci nimic ce ar putea strica chatul. Propunerile se compară alăturate, votul dă un moment de suspans, iar dezvăluirea numelor oferă o încheiere clară. Este și modul cel mai scurt.',
          ],
        },
        {
          heading: 'Și celelalte moduri',
          blocks: [
            'Nimic nu obligă la un mod dedicat, dar atunci merită să joci cu prieteni și nu cu chatul. În toate celelalte moduri streamerul își pictează zona pe ecran: spectatorii o văd cum se construiește și cunosc răspunsul înainte ca vânătoarea să înceapă.',
            'O partidă între prieteni, în schimb, se urmărește foarte bine din afară: faza de desen arată cum fiecare își construiește ascunzătoarea, iar dezvăluirea scoate la iveală toate zonele dintr-o dată, cu numele autorului.',
          ],
        },
        {
          heading: 'Cum aduci chatul în partidă',
          blocks: [
            'Linkul de invitație este singurul lucru de împărtășit. Lipește-l în chat și primii veniți umplu masa: niciun cont de creat, nicio descărcare, nicio coadă.',
            'Limita de jucători se reglează în salon, între 2 și 10. Cu un public numeros, deschide mai multe partide scurte în loc de una lungă: o rundă durează mai puțin de trei minute.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'Noutățile din Retusch',
      date: '2026-08-21',
      summary:
        'Schimbările de joc aduse în Retusch, de la cea mai recentă la cea mai veche: moduri noi, reguli noi, îmbunătățiri la desen și la vânătoare.',
      sections: [
        {
          heading: 'August 2026',
          blocks: [
            {
              list: [
                'Zona de repictat nu mai pornește de la un pătrat alb, ci de la culoarea dominantă a ceea ce acoperă. Nu se mai pierde jumătate din timp repictând un fundal care nu există în tablou.',
                'Găleata nu mai lasă o margine deschisă la culoare de-a lungul tușelor: acum alunecă pe sub marginea lor estompată.',
                'Battle royale nu mai numără puncte. Primul jucător descoperit părăsește partida, iar fiecare primește un loc pe rundă, afișat pe zona lui, de la roșu la verde.',
                'Modul stream îi dă streamerului câte încercări vrea: prima greșeală nu mai închide runda.',
                'În modul spectator se poate acum deplasa și mări tabloul pe care îl privești cum se construiește.',
                'Reacțiile emoji pornesc de la numele autorului lor, din lista jucătorilor, în loc să urce de la marginea de jos a ecranului.',
                'În afara zonei de repictat, pensula și găleata deplasează vederea: nu mai trebuie să iei unealta mână ca să te miști.',
                'Numele jocului, în stânga sus în timpul unei partide, duce înapoi la pagina de pornire după confirmare.',
                'Japoneza și coreeana se adaugă celor cincisprezece limbi deja disponibile.',
              ],
            },
          ],
        },
        {
          heading: 'Iulie 2026',
          blocks: [
            {
              list: [
                'Mod nou concurs: toți repictează aceeași zonă, apoi fiecare votează retușul care îi place cel mai mult.',
                'Mod nou stream, gândit pentru direct: gazda caută singură în timp ce publicul ei se ascunde.',
                'Categoriile de imagini înlocuiesc nivelurile de dificultate. Bifezi ce vrei să vezi, iar fiecare categorie arată un tablou de exemplu.',
                'Două runde la rând nu mai cad pe același tablou, iar categoriile bifate alternează de la o rundă la alta.',
                'Galeria de la finalul partidei permite revederea unui retuș tușă cu tușă și exportarea lui ca imagine sau ca GIF animat.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'Pe scurt',
    allArticles: 'Toate articolele',
    theRules: 'Regulile jocului',
    theModes: 'Modurile de joc',
  },
};
